import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarDays, CornerDownLeft, SlidersHorizontal, UserRound } from 'lucide-react'
import { SchedulePanel } from './TaskActionMenus'
import { AssigneeComboBox } from './AssigneeComboBox'
import { formatTaskDueDate } from './TaskList'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  PICKER_SIDE_OFFSET,
  pickerPopoverClassName,
} from '@/components/app/combo-box/PickerPanel'
import { useMembers } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { cn } from '@/lib/utils'

export type SubTaskDraft = { name: string; userId: string | null; dueDate: number | null }

interface SubTaskQuickAddProps {
  onSubmit: (draft: SubTaskDraft) => Promise<unknown>
  onCancel: () => void
  /** Opens the full form (status, priority, labels, files…) with the typed title. */
  onMoreOptions: (name: string) => void
  showAssign: boolean
  /** The parent's project: suggests its people and decides whether the Super Agent is offered. */
  projectId: string | null
  allowSuperAgent: boolean
}

/**
 * Adding a sub-task inline (design 28b): type the title, and assignee and due
 * date are optional chips. ↵ saves and opens the next line.
 */
export function SubTaskQuickAdd({ onSubmit, onCancel, onMoreOptions, showAssign, projectId, allowSuperAgent }: SubTaskQuickAddProps) {
  const { t } = useTranslation('tasks')
  const currentUserId = useLoginStore((state) => state.userId)
  const { members } = useMembers()
  const inputRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [userId, setUserId] = useState<string | null>(currentUserId)
  const [dueDate, setDueDate] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [dateOpen, setDateOpen] = useState(false)

  const assigneeLabel = (() => {
    if (!userId) return t('filters.unassigned')
    if (userId === currentUserId) return t('detailHeader.you')
    const member = members.find((m) => m.userId === userId)
    return member?.name ?? member?.email ?? t('detailHeader.someone')
  })()

  const apply = (updates: Partial<TaskDocument>) => {
    if ('userId' in updates) setUserId(updates.userId ?? null)
    if ('dueDate' in updates) setDueDate(updates.dueDate ?? null)
    inputRef.current?.focus()
  }

  const submit = async () => {
    if (!name.trim()) {
      onCancel()
      return
    }
    setSaving(true)
    try {
      await onSubmit({ name, userId, dueDate })
      // Keep assignee and date for the next line; clear the title.
      setName('')
    } finally {
      setSaving(false)
      inputRef.current?.focus()
    }
  }

  const chip =
    'inline-flex h-6 shrink-0 items-center gap-1 rounded-md border px-1.5 text-[12px] text-muted-foreground transition-colors hover:text-foreground data-[state=open]:text-foreground'

  return (
    <div className="flex h-10 items-center gap-2 rounded-md border bg-card px-2 shadow-xs">
      <span className="size-[18px] shrink-0 rounded-full border-[1.5px] border-dashed border-muted-foreground/50" />
      <input
        ref={inputRef}
        autoFocus
        value={name}
        disabled={saving}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
            event.preventDefault()
            void submit()
          } else if (event.key === 'Escape') {
            event.preventDefault()
            onCancel()
          }
        }}
        placeholder={t('subTask.placeholder')}
        aria-label={t('subTask.placeholder')}
        className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground/70 md:text-[13.5px]"
      />
      {showAssign && (
        <AssigneeComboBox
          projectId={projectId}
          selectedUserId={userId}
          onUserChange={(value) => apply({ userId: value })}
          allowSuperAgent={allowSuperAgent}
          buttonClassName={cn(chip, 'h-6 bg-transparent px-1.5 font-normal shadow-none')}
          triggerContent={
            <>
              <UserRound className="size-3" />
              <span className="max-w-24 truncate">{assigneeLabel}</span>
            </>
          }
          triggerProps={{ 'aria-label': t('bulk.assign') }}
        />
      )}
      <Popover open={dateOpen} onOpenChange={setDateOpen}>
        <PopoverTrigger className={cn(chip, dueDate != null && 'text-foreground')} aria-label={t('bulk.schedule')}>
          <CalendarDays className="size-3" />
          {dueDate != null ? formatTaskDueDate(dueDate) : t('subTask.due')}
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={PICKER_SIDE_OFFSET} className={pickerPopoverClassName}>
          <SchedulePanel
            onApply={(updates) => {
              setDateOpen(false)
              apply(updates)
            }}
          />
        </PopoverContent>
      </Popover>
      <button
        type="button"
        className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
        aria-label={t('subTask.moreOptions')}
        title={t('subTask.moreOptions')}
        onClick={() => onMoreOptions(name)}
      >
        <SlidersHorizontal className="size-3.5" />
      </button>
      <kbd className="flex h-5 shrink-0 items-center rounded border px-1 text-muted-foreground" title={t('subTask.enterHint')}>
        <CornerDownLeft className="size-3" />
      </kbd>
    </div>
  )
}
